// src/pages/Delivery/ChequeWallet.jsx
import React, { useState, useEffect, useCallback } from "react";
import Header from "../../../components/layout/Header/Header";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import DirhamSymbol from "../../../Assets/aed-symbol.png";
import toast from "../../../utils/toast";
import TableScrollSync from "../../../components/common/TableScrollSync";
import "./Wallet.css";
import axios from "axios";
import { useAppSettings } from "../../../context/AppSettingsContext";
import { usePaginatedData } from "../../../hooks/usePagination";
import Pagination from "../../../components/common/Pagination";

// 💎 Animated Icons
const ChequeIcon = () => (
  <svg className="icon-cheque" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M7 8h10M7 12h6M7 16h4" />
    <path d="M17 16l2 2" />
  </svg>
);

const SearchIcon = () => (
  <svg className="icon-search" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const PrintIcon = () => (
  <svg className="icon-print" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9V2h12v7" />
    <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" rx="1" />
  </svg>
);

const SendIcon = () => (
  <svg className="icon-send" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 2L11 13" />
    <path d="M22 2L15 22L11 13L2 9L22 2Z" />
  </svg>
);

const BankIcon = () => (
  <svg className="icon-bank" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
  </svg>
);

const ChequeWallet = () => {
  const [walletData, setWalletData] = useState({ totalAmount: 0, transactions: [] });
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeItem, setActiveItem] = useState("Cheque Wallet");
  const [user, setUser] = useState(null);
  const [payingTxId, setPayingTxId] = useState(null);
  const [printingTxId, setPrintingTxId] = useState(null);
  const [selectedTxIds, setSelectedTxIds] = useState([]);
  const [searchCustomer, setSearchCustomer] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [txToConfirm, setTxToConfirm] = useState(null);
  const [isBulkRequest, setIsBulkRequest] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_IP;

  const fetchCurrentUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return (window.location.href = "/login");
      const response = await axios.get(`${backendUrl}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(response.data.user || response.data);
    } catch (error) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
  }, [backendUrl]);

  const fetchChequeWallet = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${backendUrl}/api/wallet/delivery/cheque-wallet`, { headers: { Authorization: `Bearer ${token}` } });
      setWalletData(response.data);
    } catch (error) {
      console.error("Error fetching cheque wallet:", error);
      toast.error("Failed to load cheque wallet details");
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchCurrentUser();
    fetchChequeWallet();
  }, [fetchCurrentUser, fetchChequeWallet]);

  const handleRequestPayChequeToAdmin = (transactionId = null, isBulk = false) => {
    setIsBulkRequest(isBulk);
    setTxToConfirm(transactionId);
    setShowConfirmModal(true);
  };

  const confirmRequestPayChequeToAdmin = async () => {
    const idsToProcess = isBulkRequest ? selectedTxIds : [txToConfirm];
    if (idsToProcess.length === 0) return;

    setShowConfirmModal(false);
    setPayingTxId(isBulkRequest ? 'bulk' : txToConfirm);

    try {
      const token = localStorage.getItem("token");
      for (const id of idsToProcess) {
        await axios.post(`${backendUrl}/api/wallet/delivery/request-pay-cheque-to-admin`, { transactionId: id }, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(`${idsToProcess.length > 1 ? '✅ Bulk requests' : '✅ Request'} sent to admin!`);
      fetchChequeWallet();
      setSelectedTxIds([]);
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.message || "Failed to send request(s)");
    } finally {
      setPayingTxId(null);
      setTxToConfirm(null);
      setIsBulkRequest(false);
    }
  };

  const handlePrintReceipt = async (transactionId = null, isBulk = false) => {
    const idsToPrint = isBulk ? selectedTxIds : [transactionId];
    if (idsToPrint.length === 0) return;

    try {
      setPrintingTxId(isBulk ? 'bulk' : transactionId);
      const token = localStorage.getItem("token");
      if (!token) { toast.error("Please login again"); return; }

      const response = await axios.post(`${backendUrl}/api/wallet/receipt/bulk`, { transactionIds: idsToPrint }, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, "_blank");
      
      if (!newWindow) {
        const link = document.createElement("a");
        link.href = url;
        link.download = `cheque-receipts-${isBulk ? 'bulk' : transactionId?.slice(-8)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("📄 Receipt downloaded!");
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      if (isBulk) setSelectedTxIds([]);
    } catch (err) {
      console.error("Receipt error:", err);
      if (err.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("token");
        window.location.href = "/login";
      } else {
        toast.error("Failed to generate receipt");
      }
    } finally {
      setPrintingTxId(null);
    }
  };

  const handleSelectAll = (e) => setSelectedTxIds(e.target.checked ? filteredTransactions.map(tx => tx._id) : []);
  const handleSelectTx = (id) => setSelectedTxIds(prev => prev.includes(id) ? prev.filter(txId => txId !== id) : [...prev, id]);

  const { entriesPerPage, invoiceSystemActive } = useAppSettings();
  // A transaction "has" an invoice once its order carries one — once
  // InvoiceCounter is deleted those rows are dropped entirely.
  const txHasInvoice = (tx) =>
    !!tx.order?.invoiceNumber || !!tx.order?.deliveredInvoiceNumber || !!tx.invoiceNumber || !!tx.displayInvoiceNumber;

  const filteredTransactions = walletData.transactions.filter(tx =>
    (invoiceSystemActive || !txHasInvoice(tx)) &&
    (!searchCustomer.trim() || tx.order?.customer?.name?.toLowerCase().includes(searchCustomer.toLowerCase()))
  );

  const totalSelected = selectedTxIds.reduce((sum, id) => {
    const tx = walletData.transactions.find(t => t._id === id);
    return sum + (tx?.amount || 0);
  }, 0);

  // "Total Cheque Collected" comes straight from the backend for the whole
  // dataset — once invoiced rows are hidden, recompute it from what's
  // actually visible so the summary card matches the table.
  const visibleTotalAmount = invoiceSystemActive
    ? walletData.totalAmount
    : walletData.transactions.filter(tx => !txHasInvoice(tx)).reduce((sum, tx) => sum + tx.amount, 0);
  const pagination = usePaginatedData(filteredTransactions, entriesPerPage, `${searchCustomer}`);

  const renderChequeDetails = (details) => {
    if (!details || typeof details !== 'object') return <span className="wallet-text-muted">—</span>;
    return (
      <div className="wallet-cheque-details-mini">
        <div className="wallet-cheque-detail-row"><BankIcon /><span>{details.bank || "N/A"}</span></div>
        <div className="wallet-cheque-detail-row"><span>#{details.number || "N/A"}</span><span>{details.date ? new Date(details.date).toLocaleDateString('en-GB') : ""}</span></div>
      </div>
    );
  };

  if (!user) return <div className="wallet-loading">Loading...</div>;

  return (
    <div className="wallet-layout">
      <Header sidebarOpen={sidebarOpen} onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} user={user} />
      <Sidebar isOpen={sidebarOpen} activeItem={activeItem} onSetActiveItem={setActiveItem} onClose={() => setSidebarOpen(false)} user={user} />
      
      <main className={`wallet-main-content ${sidebarOpen ? "sidebar-open" : ""}`}>
        <div className="wallet-container-wrapper">
          <div className="wallet-container">
            
            {/* ✨ Header Section */}
            <div className="wallet-header">
              <div className="wallet-header-content">
                <div className="wallet-header-icon cheque">
                  <ChequeIcon />
                </div>
                <div>
                  <h1 className="wallet-page-title">Cheque Wallet</h1>
                  <p className="wallet-page-subtitle">Manage your collected cheque payments</p>
                </div>
              </div>
              <button className="wallet-refresh-btn" onClick={fetchChequeWallet} disabled={loading}>
                <span className="wallet-btn-icon">⟳</span>
                {loading ? "Refreshing..." : "Refresh"}
              </button>
            </div>

            {/* ✨ Summary Card */}
            <div className="wallet-summary-card cheque">
              <div className="wallet-summary-card-content">
                <div className="wallet-summary-card-icon">
                  <img src={DirhamSymbol} alt="AED" className="wallet-dirham-icon-large" />
                </div>
                <div className="wallet-summary-card-details">
                  <span className="wallet-summary-card-label">Total Cheque Collected</span>
                  <span className="wallet-summary-card-amount">
                    AED {visibleTotalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
              <div className="wallet-summary-card-glow" />
            </div>

            {/* ✨ Search Bar */}
            <div className="wallet-search-wrapper">
              <div className="wallet-search-input-group">
                <SearchIcon />
                <input type="text" className="wallet-search-input" placeholder="Search by customer name..." value={searchCustomer} onChange={(e) => setSearchCustomer(e.target.value)} />
                {searchCustomer && <button className="wallet-search-clear-btn" onClick={() => setSearchCustomer("")} title="Clear search">✕</button>}
              </div>
              <span className="wallet-search-results-count">{filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''} found</span>
            </div>

            {/* ✨ Bulk Actions Bar */}
            {selectedTxIds.length > 0 && (
              <div className="wallet-bulk-actions-bar">
                <div className="wallet-bulk-selection-info">
                  <span className="wallet-bulk-count-badge">{selectedTxIds.length}</span>
                  <span>selected • Total: <strong>AED {totalSelected.toFixed(2)}</strong></span>
                </div>
                <div className="wallet-bulk-actions-buttons">
                  <button className="wallet-btn wallet-btn-primary wallet-btn-sm" onClick={() => handleRequestPayChequeToAdmin(null, true)} disabled={payingTxId === 'bulk'}>
                    <SendIcon /> {payingTxId === 'bulk' ? "Sending..." : "Send to Admin"}
                  </button>
                  <button className="wallet-btn wallet-btn-secondary wallet-btn-sm" onClick={() => handlePrintReceipt(null, true)} disabled={printingTxId === 'bulk'}>
                    <PrintIcon /> {printingTxId === 'bulk' ? "Generating..." : "Print Receipts"}
                  </button>
                  <button className="wallet-btn wallet-btn-ghost wallet-btn-sm" onClick={() => setSelectedTxIds([])}>Clear</button>
                </div>
              </div>
            )}

            {/* ✨ Transactions Table */}
            {loading ? (
              <div className="wallet-skeleton">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="wallet-skeleton-row">
                    <div className="wallet-skeleton-cell wallet-skeleton-checkbox" />
                    <div className="wallet-skeleton-cell wallet-skeleton-text short" />
                    <div className="wallet-skeleton-cell wallet-skeleton-text" />
                    <div className="wallet-skeleton-cell wallet-skeleton-text short" />
                    <div className="wallet-skeleton-cell wallet-skeleton-text short" />
                    <div className="wallet-skeleton-cell wallet-skeleton-cheque" />
                    <div className="wallet-skeleton-cell wallet-skeleton-badge" />
                    <div className="wallet-skeleton-cell wallet-skeleton-button" />
                  </div>
                ))}
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="wallet-empty-state">
                <div className="wallet-empty-state-icon">📄</div>
                <h3>No cheque transactions found</h3>
                <p>{searchCustomer ? "Try adjusting your search" : "Cheque payments will appear here once received"}</p>
              </div>
            ) : (
              <>
                <TableScrollSync>
                  <div className="wallet-table-container">
                    <table className="wallet-data-table">
                      <thead>
                        <tr>
                          <th className="wallet-col-checkbox"><input type="checkbox" checked={selectedTxIds.length === filteredTransactions.length && filteredTransactions.length > 0} onChange={handleSelectAll} className="wallet-checkbox-custom" /></th>
                          <th className="wallet-col-index">No</th>
                          <th className="wallet-col-customer">Customer</th>
                          <th className="wallet-col-order">Order ID</th>
                          <th className="wallet-col-amount">Amount</th>
                          <th className="wallet-col-return-credit">Return Credit Used</th>
                          <th className="wallet-col-cheque">Cheque Details</th>
                          <th className="wallet-col-date">Date</th>
                          <th className="wallet-col-status">Status</th>
                          <th className="wallet-col-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagination.pageData.map((tx, index) => (
                          <tr key={tx._id} className={`wallet-tx-row ${selectedTxIds.includes(tx._id) ? 'wallet-tx-row-selected' : ''}`}>
                            <td className="wallet-col-checkbox"><input type="checkbox" checked={selectedTxIds.includes(tx._id)} onChange={() => handleSelectTx(tx._id)} className="wallet-checkbox-custom" disabled={tx.status !== 'received'} /></td>
                            <td className="wallet-col-index">{pagination.showingFrom + index}</td>
                            <td className="wallet-col-customer">
                              <div className="wallet-customer-cell">
                                <div className="wallet-customer-avatar">{tx.order?.customer?.name?.charAt(0)?.toUpperCase() || '?'}</div>
                                <span className="wallet-customer-name">{tx.order?.customer?.name || "N/A"}</span>
                              </div>
                            </td>
                            <td className="wallet-col-order"><code className="wallet-order-id">#{tx.order?.orderId || tx.order?._id?.slice(-8)}</code></td>
                            <td className="wallet-col-amount">
                              <div className="wallet-amount-cell">
                                <img src={DirhamSymbol} alt="AED" className="wallet-dirham-icon-small" />
                                <span className="wallet-amount-value">{tx.amount.toFixed(2)}</span>
                              </div>
                            </td>
                            <td className="wallet-col-return-credit">
                              {tx.returnCreditUsed > 0
                                ? <span style={{ color: "#1d4ed8", fontWeight: 600 }}>AED {tx.returnCreditUsed.toFixed(2)}</span>
                                : <span style={{ color: "#9ca3af" }}>—</span>}
                            </td>
                            <td className="wallet-col-cheque">{renderChequeDetails(tx.chequeDetails)}</td>
                            <td className="wallet-col-date">{new Date(tx.date).toLocaleDateString('en-GB')}</td>
                            <td className="wallet-col-status">
                              <span className={`wallet-status-badge wallet-status-${tx.status}`}>
                                {tx.status === "received" && <span className="wallet-status-dot pulse" />}
                                {tx.status === "received" ? "Ready to Send" : tx.status === "pending" ? "Pending Approval" : "Paid to Admin"}
                              </span>
                            </td>
                            <td className="wallet-col-actions">
                              <div className="wallet-row-actions">
                                {tx.status === "received" && (
                                  <button className="wallet-btn-action wallet-btn-send" onClick={() => handleRequestPayChequeToAdmin(tx._id)} disabled={payingTxId === tx._id} title="Request Pay to Admin">
                                    {payingTxId === tx._id ? "⏳" : <SendIcon />}
                                  </button>
                                )}
                                {tx.status === "pending" && <span className="wallet-btn-action wallet-btn-disabled" title="Awaiting admin approval">⏳</span>}
                                <button className="wallet-btn-action wallet-btn-print" onClick={() => handlePrintReceipt(tx._id)} disabled={printingTxId === tx._id} title="Print Receipt">
                                  {printingTxId === tx._id ? "⏳" : <PrintIcon />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TableScrollSync>

                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalRecords={pagination.totalRecords}
                  showingFrom={pagination.showingFrom}
                  showingTo={pagination.showingTo}
                  canPrev={pagination.canPrev}
                  canNext={pagination.canNext}
                  onPrev={pagination.goPrev}
                  onNext={pagination.goNext}
                />
              </>
            )}
          </div>
        </div>
      </main>

      {/* ✨ Confirmation Modal */}
      {showConfirmModal && (
        <div className="wallet-modal-backdrop">
          <div className="wallet-modal-content">
            <div className="wallet-modal-header">
              <div className="wallet-modal-icon wallet-confirm-icon">⚠️</div>
              <h3 className="wallet-modal-title">{isBulkRequest ? `Confirm Bulk Cheque Request` : "Confirm Cheque Request"}</h3>
            </div>
            <div className="wallet-modal-body">
              <p className="wallet-modal-text">
                {isBulkRequest ? `You're about to request admin approval for handing over ` : `You're about to request approval for handing over the cheque of `}
                <strong className="wallet-amount-highlight">AED {(isBulkRequest ? totalSelected : walletData.transactions.find(t => t._id === txToConfirm)?.amount)?.toFixed(2)}</strong>
                {isBulkRequest ? ` from ${selectedTxIds.length} transaction${selectedTxIds.length !== 1 ? 's' : ''}` : ''} to the admin.
              </p>
              <div className="wallet-modal-info-box">
                <span className="wallet-info-icon">ℹ️</span>
                <span>Ensure the physical cheque is ready for handover. Status will change to "Pending Approval" upon submission.</span>
              </div>
            </div>
            <div className="wallet-modal-footer">
              <button className="wallet-btn wallet-btn-ghost" onClick={() => setShowConfirmModal(false)}>Cancel</button>
              <button className="wallet-btn wallet-btn-primary" onClick={confirmRequestPayChequeToAdmin} disabled={payingTxId}>
                {payingTxId ? "Processing..." : "✅ Yes, Request Approval"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChequeWallet;